import React, { FC, useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box, IconButton } from '@mui/material';
import Heading from '../../Components/Heading/heading.tsx';
import { editWarehouses } from './action.ts';
import { validateEmail } from '../../Utils/EmailValidation/email-validation.tsx';
import { validateDriverName } from '../../Utils/NameValidation/name-validation.tsx';
import { validateRemarks } from '../../Utils/RemarksValidation/remarks-validation.tsx';
import { validatePhoneNumber } from '../../Utils/PhoneNumberValidation/phone-number-validation.tsx';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MapModal from './map-modal.tsx';

interface AddWarehousesModalProps {
    open: boolean;
    onClose: () => void;
    warehouse: any;
}

const EditWarehousesModal: FC<AddWarehousesModalProps> = ({ open, onClose, warehouse }) => {
    const [warehouseName, setWarehouseName] = useState('');
    const [contactName, setContactName] = useState<string>('');
    const [warehouseNameError, setWarehouseNameError] = useState<string>('');
    const [contactNameError, setContactNameError] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [remarks, setRemarks] = useState<string>('');
    const [emailError, setEmailError] = useState<string>('');
    const [remarksError, setRemarksError] = useState<string>('');
    const [mobileNumber, setMobileNumber] = useState<string>('');
    const [mobileNumberError, setMobileNumberError] = useState<string>('');
    const [latitude, setLatitude] = useState<string>('');
    const [longitude, setLongitude] = useState<string>('');
    const [latitudeError, setLatitudeError] = useState<string>('');
    const [longitudeError, setLongitudeError] = useState<string>('');
    const [mapModalOpen, setMapModalOpen] = useState(false);

    useEffect(() => {
        if (open) {
          console.log(warehouse)
            setWarehouseName(warehouse.warehouse);
            setContactName(warehouse.contactName);
            setMobileNumber(warehouse.mobileNumber);
            setEmail(warehouse.email);
            setRemarks(warehouse.remarks);
            setLatitude(warehouse.latitude || '');
            setLongitude(warehouse.longitude || '');
        }
    }, [open, warehouse]);

    const handleEmailBlur = () => {
        if (email === '') {
            setEmailError('');
        } else if (validateEmail(email)) {
            setEmailError('');
        } else {
            setEmailError('Please enter a valid email address.');
        }
    };

    const handleLocationSelect = ( lat: string, lng: string) => {
        setLatitude(lat);
        setLongitude(lng);
        setLatitudeError('');
        setLongitudeError('');
        setMapModalOpen(false);
    };

    const handleGoogleMapsSelect = () => {
        setMapModalOpen(true);
    };

    const validateLatitude = (value: string): boolean => {
        const num = parseFloat(value);
        return !isNaN(num) && num >= -90 && num <= 90;
    };

    const validateLongitude = (value: string): boolean => {
        const num = parseFloat(value);
        return !isNaN(num) && num >= -180 && num <= 180;
    };

    const handleLatitudeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setLatitude(value);
        if (value === '') {
            setLatitudeError('');
        } else if (validateLatitude(value)) {
            setLatitudeError('');
        } else {
            setLatitudeError('Invalid latitude (-90 to 90)');
        }
    };

    const handleLongitudeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setLongitude(value);
        if (value === '') {
            setLongitudeError('');
        } else if (validateLongitude(value)) {
            setLongitudeError('');
        } else {
            setLongitudeError('Invalid longitude (-180 to 180)');
        }
    };

    const handleSave = () => {
        let hasError = false;

        if (!warehouseName) {
            setWarehouseNameError('Warehouse Name is required');
            hasError = true;
        }
        if (!contactName) {
            setContactNameError('Contact Name is required');
            hasError = true;
        }
        if (!mobileNumber) {
            setMobileNumberError('Mobile number is required');
            hasError = true;
        }
        if (!latitude) {
            setLatitudeError('Latitude is required');
            hasError = true;
        }
        if (!longitude) {
            setLongitudeError('Longitude is required');
            hasError = true;
        }
        if (!email) {
            setEmailError('Email is required');
            hasError = true;
        }

        if (hasError) {
            toast.error("Please fix the errors");
            return;
        }

        editWarehouses(
            warehouseName,
            contactName,
            latitude,
            longitude,
            remarks,
            mobileNumber,
            email,
            warehouse.id,
            (response) => {
                toast.success(response.message.message);
                setTimeout(() => {
                    window.location.reload();
                }, 3000);
            },
            (errorMessage) => {
                toast.error(errorMessage);
            }
        );
    };

    const handleContactNameChange = (event) => {
        const { value } = event.target;
        setContactName(value);
        if (value === '') {
            setContactNameError('');
        } else if (validateDriverName(value)) {
            setContactNameError('');
        } else {
            setContactNameError('Characters are allowed');
        }
    };

    const handleWarehouseNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setWarehouseName(value);
        if (value === '') {
            setWarehouseNameError('');
        } else if (validateRemarks(value)) {
            setWarehouseNameError('');
        } else {
            setWarehouseNameError('Characters are allowed');
        }
    };

    const handleEmailChange = (event) => {
        const { value } = event.target;
        setEmail(value);
        setEmailError('');
    };

    const handleRemarksChange = (event) => {
        const { value } = event.target;
        setRemarks(value);
        if (value === '') {
            setRemarksError('');
        } else if (validateRemarks(value)) {
            setRemarksError('');
        } else {
            setRemarksError('Invalid Character');
        }
    };

    const handlePhoneNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setMobileNumber(value);

        if (value === '') {
            setMobileNumberError('');
            return;
        }

        const { errorMessage } = validatePhoneNumber(value);
        setMobileNumberError(errorMessage || '');
    };

    const handlePhoneNumberBlur = () => {
        if (mobileNumber === '') {
            setMobileNumberError('');
        } else if (mobileNumber.length >= 1 && mobileNumber.length < 10) {
            setMobileNumberError('Please enter a 10 digit number');
        }
    };

    const handleWarehouseNameBlur = () => {
        if (warehouseNameError === '') {
            setWarehouseNameError('');
        } else {
            setWarehouseNameError(warehouseNameError);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <Box sx={{ marginLeft: { xs: '0px', sm: '-180px' } }}>
                <Heading title="Edit Warehouses" closeIcon="true" onClose={onClose} />
            </Box>
          <DialogContent style={{ padding: '20px' }}>
    <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 2, 
        alignItems: 'flex-start',
        overflowX: 'auto',  // This enables horizontal scrolling
        width: '100%',     // Ensure it takes full width
        '&::-webkit-scrollbar': {
            height: '6px',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#888',
            borderRadius: '3px',
        },
    }}>
        {/* Rest of your content remains the same */}
        <Box sx={{ display: 'flex', gap: 2, width: 'max-content', paddingBottom: '10px', marginLeft: '40px',marginTop:'10px' }}>
            <TextField
                label="Warehouse Name"
                size='small'
                value={warehouseName}
                onChange={handleWarehouseNameChange}
                error={!!warehouseNameError}
                helperText={warehouseNameError}
                variant="outlined"
            />
            <TextField
                label="Contact Name"
                value={contactName}
                size='small'
                onChange={handleContactNameChange}
                error={!!contactNameError}
                helperText={contactNameError}
                variant="outlined"
            />
        </Box>

        <Box sx={{ display: 'flex', gap: 2, width: 'max-content', paddingBottom: '10px', marginLeft: '40px' }}>
            <TextField
                label="Mobile Number"
                value={mobileNumber}
                size='small'
                onChange={handlePhoneNumberChange}
                onBlur={handlePhoneNumberBlur}
                error={!!mobileNumberError}
                helperText={mobileNumberError}
                variant="outlined"
            />
            <TextField
                label="Email"
                value={email}
                size='small'
                onChange={handleEmailChange}
                onBlur={handleEmailBlur}
                error={!!emailError}
                helperText={emailError}
                variant="outlined"
            />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: 'max-content', paddingBottom: '10px' }}>
            <IconButton
                onClick={handleGoogleMapsSelect}
                color="primary"
                aria-label="select from map"
            >
                <LocationOnIcon />
            </IconButton>
            <TextField
                label="Latitude"
                size='small'
                value={latitude}
                onChange={handleLatitudeChange}
                error={!!latitudeError}
                helperText={latitudeError}
                variant="outlined"
            />
            <TextField
                label="Longitude"
                size='small'
                value={longitude}
                onChange={handleLongitudeChange}
                error={!!longitudeError}
                helperText={longitudeError}
                variant="outlined"
            />
        </Box>
        <Box sx={{ display: 'flex', gap: 2, width: 'max-content', paddingBottom: '10px', marginLeft: '40px' }}>
            <TextField
                label="Remarks"
                value={remarks}
                size='small'
                onChange={handleRemarksChange}
                error={!!remarksError}
                helperText={remarksError}
                variant="outlined"
            />
        </Box>
    </Box>
</DialogContent>
            <DialogActions style={{ justifyContent: 'center' }}>
                <Button
                    onClick={onClose}
                    className="custom-cancel-button"
                    style={{ marginRight: '10px' }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    className="custom-button"
                    style={{ marginLeft: '10px' }}
                >
                    Save
                </Button>
            </DialogActions>
            <MapModal
                open={mapModalOpen}
                onClose={() => setMapModalOpen(false)}
                onLocationSelect={handleLocationSelect}
                initialLocation={
                    latitude && longitude
                        ? { lat: parseFloat(latitude), lng: parseFloat(longitude) }
                        : undefined
                }
            />
        </Dialog>
    );
};

export default EditWarehousesModal;